import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import { DateTime } from 'luxon';
import { IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { MaterialDataTable } from '../../table-common/MaterialDataTable';

const columnDefinitions = [
  ['id', 'run-id', 90],
  ['startTs', 'started', 220],
  ['runDurationMillis', 'duration', 120],
  ['historyEventTotalCount', 'history-event-count', 170],
  ['occurrenceTotalCount', 'occurrence-count', 160],
  ['consistTotalCount', 'consist-count', 150],
];

export const RuleRunsTable = ({
  items = [],
  count = null,
  after,
  active = false,
  loadBusy = false,
  error = null,
  hasMore = false,
  onAfterChange,
  onLoadMore,
  onRetry,
  $translate,
  formatTimestampFilter,
  testIdPrefix = 'alarm-rule-view-rule-runs',
}) => {
  const containerRef = useRef(null);
  const invalidRawInput = useRef(false);
  const [invalidDate, setInvalidDate] = useState(false);
  const label = key => $translate.instant(`alarm-rule-view.${key}`);
  const columns = columnDefinitions.map(([accessorKey, translationKey, size]) => ({
    accessorKey,
    header: label(translationKey),
    size,
    Cell: ({ cell }) => {
      const value = cell.getValue();
      if (value == null || value === '') return '-';
      if (accessorKey === 'startTs' || accessorKey === 'expiresTs') return formatTimestampFilter(value);
      return typeof value === 'number' ? value.toLocaleString() : value;
    },
  }));

  useEffect(() => {
    if (containerRef.current) containerRef.current.scrollTop = 0;
    setInvalidDate(false);
  }, [after]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !active || loadBusy || error || !hasMore) return;
    const fillViewport = () => {
      if (container.clientHeight > 0 && container.scrollHeight <= container.clientHeight + 1) onLoadMore();
    };
    const frame = requestAnimationFrame(fillViewport);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fillViewport);
    observer?.observe(container);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [active, loadBusy, error, hasMore, items, onLoadMore]);

  const handleDateChange = (date, event) => {
    if (event?.type === 'change' && invalidRawInput.current) return;
    const invalid = !(date instanceof Date) || !Number.isFinite(date.getTime()) || date.getTime() > Date.now();
    setInvalidDate(invalid);
    if (!invalid) onAfterChange(date);
  };

  const handleRawDateChange = event => {
    const value = event?.target?.value;
    if (event?.type !== 'change' || typeof value !== 'string') {
      invalidRawInput.current = false;
      return;
    }
    const parsed = DateTime.fromFormat(value, 'dd/MM/yyyy HH:mm');
    const invalid = !/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/.test(value)
      || !parsed.isValid || parsed.toMillis() > Date.now();
    invalidRawInput.current = invalid;
    setInvalidDate(invalid);
  };

  return (
    <div className="alarm-rule-runs" data-testid={`${testIdPrefix}-container`}>
      <div className="alarm-rule-runs__toolbar" data-testid={`${testIdPrefix}-toolbar`}>
        <div className="alarm-rule-runs__date">
          <label htmlFor={`${testIdPrefix}-after`}>{label('since')}</label>
          <DatePicker
            popperPlacement="bottom-start"
            id={`${testIdPrefix}-after`}
            selected={after}
            maxDate={new Date()}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            dateFormat="dd/MM/yyyy HH:mm"
            showIcon
            toggleCalendarOnIconClick
            onChange={handleDateChange}
            onChangeRaw={handleRawDateChange}
            popperClassName="alarm-rule-runs__date-popper"
            customInput={(
              <input
                className="form-control"
                data-testid={`${testIdPrefix}-after`}
                aria-invalid={invalidDate}
                aria-describedby={invalidDate ? `${testIdPrefix}-date-error` : undefined}
              />
            )}
          />
        </div>
        <p className="alarm-rule-runs__count" data-testid={`${testIdPrefix}-count`} aria-live="polite">
          <strong>{label('total')}:</strong> {count == null ? '-' : count.toLocaleString()}
          {count != null && <span> ({label('loaded')}: {items.length.toLocaleString()})</span>}
        </p>
      </div>
      {invalidDate && (
        <p id={`${testIdPrefix}-date-error`} className="form-validator-text-danger" role="alert"
          data-testid={`${testIdPrefix}-date-error`}>
          {label('invalid-date')}
        </p>
      )}
      {loadBusy && (
        <output className="alarm-rule-runs__loading" data-testid={`${testIdPrefix}-loading`}>
          <i className="fas fa-spinner fa-pulse fa-fw" /> {label('loading')}
        </output>
      )}
      {error && (
        <div className="alarm-rule-runs__error" data-testid={`${testIdPrefix}-error`}>
          <span className="form-validator-text-danger" role="alert">{label(error)}</span>
          <Tooltip title={label('retry')}>
            <IconButton size="small" aria-label={label('retry')} onClick={onRetry} disabled={loadBusy}
              data-testid={`${testIdPrefix}-retry`}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      )}
      {!loadBusy && !error && count != null && items.length === 0 && (
        <p data-testid={`${testIdPrefix}-empty`}>{label('empty')}</p>
      )}
      <MaterialDataTable
        className="event-history-container consist-event-history alarm-rule-runs__table"
        tableTestId={`${testIdPrefix}-table`}
        scrollContainerClassName="alarm-rule-runs__scroll"
        tableContainerRef={containerRef}
        ariaLabel={label('table-label')}
        columns={columns}
        data={items}
        getRowId={row => String(row.id)}
        isLoading={loadBusy}
        enableRowVirtualization
        rowVirtualizerOptions={{ estimateSize: () => 40, overscan: 10 }}
        onReachBottom={onLoadMore}
        canLoadMore={active && hasMore && !error}
        muiTableBodyRowProps={({ row }) => ({
          'data-testid': `${testIdPrefix}-row-${row.original.id}`,
          className: row.index % 2 ? 'row-odd' : 'row-even',
        })}
        muiTableBodyCellProps={({ row, column }) => ({
          'data-testid': `${testIdPrefix}-cell-${row.original.id}-${column.id}`,
        })}
      />
    </div>
  );
};

RuleRunsTable.propTypes = {
  formatTimestampFilter: PropTypes.func.isRequired,
  items: PropTypes.array,
  count: PropTypes.number,
  after: PropTypes.instanceOf(Date).isRequired,
  active: PropTypes.bool,
  loadBusy: PropTypes.bool,
  error: PropTypes.string,
  hasMore: PropTypes.bool,
  onAfterChange: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  $translate: PropTypes.func.isRequired,
  testIdPrefix: PropTypes.string,
};